import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../App";
import { Card, CardContent } from "../components/ui/card";
import Logo from "../components/Logo";
import {
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  DollarSign,
  ShoppingCart,
  Users,
  CheckCircle2,
  Lock,
  ArrowRight,
  Package,
  Clock
} from "lucide-react";

// ============ ADMIN ACCESS CONTROL ============
// Only these emails can access demo pages
const DEMO_ADMIN_EMAILS = [
  "reynir@replydriven.com",
  "reyniroskarsson2310@gmail.com"
];

// Secret key for URL-based access (for screenshots without login)
const DEMO_SECRET_KEY = "rd_demo_2026";

// ============ DEMO DATA - VARIATION 1: Checkout Friction ============
const DEMO_INSIGHT_CHECKOUT = {
  store_name: "Outdoor Essentials Co.",
  date_range: "Jan 13 – Jan 19, 2026",
  metrics: {
    revenue: 24850,
    revenue_change: -8.2,
    orders: 312,
    orders_change: -12.5,
    aov: 79.65,
    aov_change: 4.9,
    returning_rate: 22,
    returning_rate_change: -3
  },
  profit_leak: {
    badge: "PROFIT LEAK",
    estimated_impact: 3200,
    title: "High cart abandonment on mobile checkout",
    what_changed: "Mobile checkout completion dropped 18% this week. Customers are adding items to cart but leaving at the payment step more frequently than desktop users.",
    why_it_matters: "Mobile traffic accounts for 68% of your sessions but only 41% of completed orders. This gap suggests friction in your mobile checkout flow that's costing you sales.",
    recommended_action: "Review your mobile checkout for unnecessary form fields, slow-loading payment options, or trust signals that may be missing. Consider enabling accelerated checkout options like Shop Pay or Apple Pay."
  },
  what_worked: {
    title: "New arrivals driving engagement",
    description: "Products added in the last 14 days generated 34% of this week's revenue, showing strong customer interest in fresh inventory."
  }
};

// ============ DEMO DATA - VARIATION 2: AOV Opportunity ============
const DEMO_INSIGHT_AOV = {
  store_name: "Nordic Home Goods",
  date_range: "Jan 13 – Jan 19, 2026",
  metrics: {
    revenue: 18420,
    revenue_change: 5.3,
    orders: 289,
    orders_change: 8.1,
    aov: 63.74,
    aov_change: -2.6,
    returning_rate: 31,
    returning_rate_change: 2
  },
  profit_leak: {
    badge: "REVENUE OPPORTUNITY",
    estimated_impact: 2800,
    title: "Single-item orders limiting average order value",
    what_changed: "72% of orders this week contained only one item. Customers browsing multiple product pages are not adding related items to their carts.",
    why_it_matters: "Your returning customers spend 2.4x more per order than first-time buyers. But even loyal customers are completing single-item purchases when your catalog suggests natural product pairings.",
    recommended_action: "Add product bundles or 'frequently bought together' recommendations. Consider a free shipping threshold slightly above your current AOV to encourage larger orders."
  },
  what_worked: {
    title: "Email campaign drove repeat purchases",
    description: "Returning customer rate increased 2 points this week, coinciding with your mid-week promotional email. Repeat buyers converted at 3.2x the rate of new visitors."
  }
};

// ============ DEMO DATA - VARIATION 3: Retention Gap ============
const DEMO_INSIGHT_RETENTION = {
  store_name: "Craft Supply Studio",
  date_range: "Jan 13 – Jan 19, 2026",
  metrics: {
    revenue: 31200,
    revenue_change: 12.4,
    orders: 456,
    orders_change: 15.2,
    aov: 68.42,
    aov_change: -2.4,
    returning_rate: 18,
    returning_rate_change: -4
  },
  profit_leak: {
    badge: "RETENTION GAP",
    estimated_impact: 4100,
    title: "First-time buyers not returning for second purchase",
    what_changed: "Returning customer rate dropped from 22% to 18% despite higher overall traffic. New customer acquisition is up, but repeat purchase behavior has declined.",
    why_it_matters: "Acquiring a new customer costs 5-7x more than retaining an existing one. Your repeat customers have a lifetime value 3x higher than one-time buyers.",
    recommended_action: "Set up a post-purchase email sequence for first-time buyers. Consider a loyalty program or targeted discount for second purchases within 30 days."
  },
  what_worked: {
    title: "Social traffic converting well",
    description: "Visitors from Instagram had a 4.2% conversion rate this week, nearly double your site average. This channel is driving quality traffic."
  }
};

// Get demo data by variation
const getDemoData = (variation) => {
  switch (variation) {
    case "aov":
      return DEMO_INSIGHT_AOV;
    case "retention":
      return DEMO_INSIGHT_RETENTION;
    case "checkout":
    default:
      return DEMO_INSIGHT_CHECKOUT;
  }
};

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// Metric Card Component
const MetricCard = ({ icon, label, value, change, isPercentagePoint = false }) => {
  const isPositive = change > 0;
  const changeColor = isPositive ? "text-emerald-400" : "text-red-400";
  const TrendIcon = isPositive ? TrendingUp : TrendingDown;
  
  return (
    <Card className="bg-surface border-[#262626]">
      <CardContent className="pt-4 pb-4">
        <div className="flex items-center gap-2 text-text-muted mb-2">
          {icon}
          <span className="text-sm">{label}</span>
        </div>
        <div className="flex items-end justify-between">
          <span className="font-heading text-2xl font-bold">{value}</span>
          {change !== null && change !== undefined && (
            <div className={`flex items-center gap-1 text-sm ${changeColor}`}>
              <TrendIcon className="w-3 h-3" />
              <span>{isPositive ? "+" : ""}{change}{isPercentagePoint ? "pp" : "%"}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const DemoWeeklyInsight = () => {
  const navigate = useNavigate();
  const { variation = "checkout" } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [authorized, setAuthorized] = useState(false);

  // Check admin access - either by email or secret URL key
  useEffect(() => {
    const secretKey = searchParams.get("key");
    
    // Allow access if:
    // 1. User is logged in with admin email, OR
    // 2. Secret key is provided in URL
    if (
      (user?.email && DEMO_ADMIN_EMAILS.includes(user.email.toLowerCase())) ||
      secretKey === DEMO_SECRET_KEY
    ) {
      setAuthorized(true);
    } else if (user !== undefined) {
      // Only redirect if auth check is complete (user is defined, even if null)
      // Wait a bit for auth to settle
      const timer = setTimeout(() => {
        if (!authorized) {
          navigate("/");
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [user, searchParams, navigate, authorized]);

  // Don't render until authorized
  if (!authorized) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <Lock className="w-12 h-12 text-text-muted mx-auto mb-4" />
          <p className="text-text-muted">Checking access...</p>
        </div>
      </div>
    );
  }

  const data = getDemoData(variation);
  const isRetention = variation === "retention";
  const badgeColor = isRetention ? "bg-blue-500" : data.profit_leak.badge === "REVENUE OPPORTUNITY" ? "bg-emerald-500" : "bg-[#FF453A]";

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Minimal Header - Screenshot Ready */}
      <header className="border-b border-[#262626] bg-[#050505]">
        <div className="max-w-6xl mx-auto px-8 py-4 flex items-center justify-between">
          <Logo showText />
          <div className="flex items-center gap-3 text-sm text-text-muted">
            <Clock className="w-4 h-4" />
            <span>{data.date_range}</span>
          </div>
        </div>
      </header>

      {/* Main Content - Optimized for 1600x900 screenshot */}
      <main className="max-w-6xl mx-auto px-8 py-8">
        {/* Store Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-[#D4F34A]/10 flex items-center justify-center">
              <Package className="w-5 h-5 text-[#D4F34A]" />
            </div>
            <div>
              <h1 className="font-heading text-xl font-bold">{data.store_name}</h1>
              <p className="text-sm text-text-muted">Weekly Profit Insight</p>
            </div>
          </div>
        </div>

        {/* Main Grid - Two columns */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left Column - Profit Leak Card (Larger) */}
          <div className="lg:col-span-3 space-y-6">
            {/* Profit Leak / Opportunity Card */}
            <Card className={`bg-gradient-to-br ${isRetention ? "from-blue-500/10 to-transparent border-blue-500/20" : data.profit_leak.badge === "REVENUE OPPORTUNITY" ? "from-emerald-500/10 to-transparent border-emerald-500/20" : "from-[#FF453A]/10 to-transparent border-[#FF453A]/20"} relative overflow-hidden`}>
              <div className={`absolute top-4 right-4 ${badgeColor} text-white text-xs font-bold px-3 py-1 rounded-full`}>
                {data.profit_leak.badge}
              </div>
              <CardContent className="pt-6 pb-6">
                <div className="flex items-start gap-4 mb-5">
                  <div className={`w-14 h-14 rounded-xl ${isRetention ? "bg-blue-500/20" : data.profit_leak.badge === "REVENUE OPPORTUNITY" ? "bg-emerald-500/20" : "bg-[#FF453A]/20"} flex items-center justify-center flex-shrink-0`}>
                    <AlertTriangle className={`w-7 h-7 ${isRetention ? "text-blue-400" : data.profit_leak.badge === "REVENUE OPPORTUNITY" ? "text-emerald-400" : "text-[#FF453A]"}`} />
                  </div>
                  <div>
                    <p className="text-text-muted text-sm mb-1">Estimated Weekly Impact</p>
                    <p className={`font-heading text-4xl font-extrabold ${isRetention ? "text-blue-400" : data.profit_leak.badge === "REVENUE OPPORTUNITY" ? "text-emerald-400" : "text-[#FF453A]"}`}>
                      ~{formatCurrency(data.profit_leak.estimated_impact)}
                    </p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-text-muted uppercase tracking-wider mb-1">Issue</p>
                    <p className="font-semibold text-lg">{data.profit_leak.title}</p>
                  </div>
                  <div>
                    <p className="text-xs text-text-muted uppercase tracking-wider mb-1">What Changed</p>
                    <p className="text-text-muted">{data.profit_leak.what_changed}</p>
                  </div>
                  <div>
                    <p className="text-xs text-text-muted uppercase tracking-wider mb-1">Why This Matters</p>
                    <p className="text-text-muted">{data.profit_leak.why_it_matters}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recommended Action */}
            <Card className="bg-gradient-to-br from-[#D4F34A]/10 to-transparent border-[#D4F34A]/20">
              <CardContent className="pt-5 pb-5">
                <div className="flex items-center gap-2 text-[#D4F34A] mb-3">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="text-sm font-medium uppercase tracking-wider">Recommended Action</span>
                </div>
                <p className="text-lg font-semibold">{data.profit_leak.recommended_action}</p>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Metrics + What Worked */}
          <div className="lg:col-span-2 space-y-6">
            {/* Metrics Grid */}
            <div className="grid grid-cols-2 gap-3">
              <MetricCard
                icon={<DollarSign className="w-4 h-4" />}
                label="Revenue"
                value={formatCurrency(data.metrics.revenue)}
                change={data.metrics.revenue_change}
              />
              <MetricCard
                icon={<ShoppingCart className="w-4 h-4" />}
                label="Orders"
                value={data.metrics.orders}
                change={data.metrics.orders_change}
              />
              <MetricCard
                icon={<DollarSign className="w-4 h-4" />}
                label="AOV"
                value={formatCurrency(data.metrics.aov)}
                change={data.metrics.aov_change}
              />
              <MetricCard
                icon={<Users className="w-4 h-4" />}
                label="Returning"
                value={`${data.metrics.returning_rate}%`}
                change={data.metrics.returning_rate_change}
                isPercentagePoint
              />
            </div>

            {/* What Worked */}
            <Card className="bg-surface border-[#262626]">
              <CardContent className="pt-5 pb-5">
                <div className="flex items-center gap-2 text-emerald-400 mb-3">
                  <TrendingUp className="w-5 h-5" />
                  <span className="text-sm font-medium uppercase tracking-wider">What Worked</span>
                </div>
                <p className="font-semibold mb-2">{data.what_worked.title}</p>
                <p className="text-text-muted text-sm">{data.what_worked.description}</p>
              </CardContent>
            </Card>

            {/* Email Schedule Note */}
            <div className="bg-surface/50 border border-[#262626] rounded-lg p-4">
              <div className="flex items-center gap-2 text-text-muted text-sm">
                <Clock className="w-4 h-4" />
                <span>Delivered every Monday at 9:00 AM</span>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Minimal Footer - Demo indicator (hidden from screenshot area) */}
      <footer className="fixed bottom-0 left-0 right-0 bg-[#050505] border-t border-[#262626] py-2 px-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between text-xs text-text-muted">
          <span>Internal Demo • Not for public distribution</span>
          <div className="flex gap-4">
            <a href="/demo/weekly-insight/checkout" className={variation === "checkout" ? "text-[#D4F34A]" : "hover:text-white"}>Checkout</a>
            <a href="/demo/weekly-insight/aov" className={variation === "aov" ? "text-[#D4F34A]" : "hover:text-white"}>AOV</a>
            <a href="/demo/weekly-insight/retention" className={variation === "retention" ? "text-[#D4F34A]" : "hover:text-white"}>Retention</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DemoWeeklyInsight;
